import { Button } from '@breetometer/components';
import { classNameMerger } from '@breetometer/utils';
import { ExportIcon, FilterIcon, FullStopIcon, SearchIcon } from 'BreetAssets';
import { DropdownButton, type IDropdownOption, PageHeader, SearchInput } from 'BreetComponents';
import { getQueryParamValue, updateUrlSearchParams } from 'BreetHelpers';
import { useModalApi, usePermissions, useWindowSize } from 'BreetHooks';
import debounce from 'lodash/debounce';
import { type ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { type IDataTableHeader, type TableHeaderFilterItemOptions } from './DataTable.helpers';

interface IMobileSearchPopover {
	close: () => void;
	searchValue: string;
	appliedSearch: string;
	placeholder?: string;
	onSearchDraft: (e: ChangeEvent<HTMLInputElement>) => void;
	onApply: (close: () => void) => void;
	onClear: (close: () => void) => void;
}

const MobileSearchPopover = ({
	close,
	searchValue,
	appliedSearch,
	placeholder,
	onSearchDraft,
	onApply,
	onClear,
}: IMobileSearchPopover) => {
	const handleApply = useCallback(() => onApply(close), [onApply, close]);
	const handleClear = useCallback(() => onClear(close), [onClear, close]);

	return (
		<div className='datatable_searchpopover dropdownshadow'>
			<SearchInput
				widthType='full'
				id='datatable-mobile-search'
				// eslint-disable-next-line jsx-a11y/no-autofocus
				autoFocus
				onChange={onSearchDraft}
				placeholder={placeholder}
				value={searchValue}
			/>
			<div className='datatable_searchpopover_actions'>
				{!!appliedSearch && (
					<button
						type='button'
						className='datatable_searchpopover_clear color-error-400'
						onClick={handleClear}
					>
						Clear
					</button>
				)}
				<Button
					onClick={handleApply}
					disabled={!searchValue.trim()}
					className='primary-hover'
				>
					Search
				</Button>
			</div>
		</div>
	);
};

export const DataTableHeader = ({
	filterProps,
	exportProps,
	filterList,
	pageTitle,
	searchProps,
	onSelectedFilterChange,
	isLoading,
	onRefresh,
	otherBtnProps,
	tip,
	counter,
	isFilter,
	activeFilters,
	onClearFilters,
	noRecord,
}: IDataTableHeader) => {
	const filterCount = activeFilters?.length ?? 0;
	const isFilterActive = filterCount > 0 || !!isFilter;
	const [selectedFilters, setSelectedFilters] = useState<Record<string, TableHeaderFilterItemOptions | undefined>>({});

	const { handleOpenModal } = useModalApi();

	const windowSizeProps = useWindowSize();

	const { hasPermission } = usePermissions();

	const [searchValue, setSearchValue] = useState<string>(searchProps?.initialValue ?? '');
	const [appliedSearch, setAppliedSearch] = useState<string>(searchProps?.initialValue ?? '');

	const debouncedSearch = useMemo(
		() =>
			debounce((onSearch: (data: Record<string, string>) => void, searchKey: string | number, value: string) => {
				onSearch({ [String(searchKey)]: value });
			}, 300),
		[]
	);

	useEffect(() => {
		if (onSelectedFilterChange) onSelectedFilterChange(selectedFilters);
	}, [onSelectedFilterChange, selectedFilters]);

	useEffect(() => {
		if (searchProps?.searchResetToken === undefined) return;
		const next = searchProps.initialValue ?? '';
		setSearchValue(next);
		setAppliedSearch(next);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchProps?.searchResetToken]);

	useEffect(() => {
		filterList?.forEach((filter) => {
			const encodedFilterOptions = getQueryParamValue(filter.id);
			if (encodedFilterOptions) {
				const decodedFilterOptions = JSON.parse(decodeURIComponent(encodedFilterOptions)) as TableHeaderFilterItemOptions;
				setSelectedFilters({ [filter.id]: decodedFilterOptions });
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const toggleSelectedFilters = useCallback((filterKey: string, selectedItem: IDropdownOption) => {
		setSelectedFilters((prevData) => {
			const { [filterKey]: existingEntry, ...newFilters } = prevData;

			if (JSON.stringify(existingEntry) === JSON.stringify(selectedItem.value)) {
				return { ...newFilters, [filterKey]: undefined };
			}

			return { ...newFilters, [filterKey]: selectedItem.value } as Record<string, TableHeaderFilterItemOptions | undefined>;
		});
	}, []);

	const handleOptionClick = useCallback(
		(filterKey: string) => (selectedItem?: IDropdownOption) => {
			if (!selectedItem) return;
			toggleSelectedFilters(filterKey, selectedItem);
			updateUrlSearchParams({ addParams: { [filterKey]: encodeURIComponent(JSON.stringify(selectedItem.value)) } });
		},
		[toggleSelectedFilters]
	);

	const onHandleSearch = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			setSearchValue(e.target.value);
			setAppliedSearch(e.target.value);

			if (searchProps?.handleReqSearch) {
				debouncedSearch(searchProps.handleReqSearch, searchProps.searchKey ?? 'search', e.target.value);
			}
		},
		[searchProps, debouncedSearch]
	);

	const onHandleSearchDraft = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setSearchValue(e.target.value);
	}, []);

	const applyMobileSearch = useCallback(
		(close: () => void) => {
			searchProps?.handleReqSearch?.({ [searchProps.searchKey ?? 'search']: searchValue });
			setAppliedSearch(searchValue);
			close();
		},
		[searchProps, searchValue]
	);

	const clearMobileSearch = useCallback(
		(close: () => void) => {
			setSearchValue('');
			setAppliedSearch('');
			searchProps?.handleReqSearch?.({ [searchProps.searchKey ?? 'search']: '' });
			close();
		},
		[searchProps]
	);

	const renderMobileSearch = useCallback(
		(close?: () => void) => (
			<MobileSearchPopover
				close={close ?? (() => undefined)}
				searchValue={searchValue}
				appliedSearch={appliedSearch}
				placeholder={searchProps?.placeHolder}
				onSearchDraft={onHandleSearchDraft}
				onApply={applyMobileSearch}
				onClear={clearMobileSearch}
			/>
		),
		[searchValue, appliedSearch, searchProps?.placeHolder, onHandleSearchDraft, applyMobileSearch, clearMobileSearch]
	);

	const exportConfig = typeof exportProps === 'object' ? exportProps : undefined;

	return (
		<>
			<div className='datatable_header'>
				<PageHeader
					pageTitle={pageTitle}
					isLoading={isLoading}
					onRefresh={onRefresh}
					counter={counter}
					tip={tip}
				/>
				<div className='datatable_headersearchfilter'>
					{!!searchProps && (
						<>
							<SearchInput
								widthType='full'
								id=''
								onChange={onHandleSearch}
								placeholder={searchProps.placeHolder}
								value={searchValue}
							/>

							<div className={classNameMerger('datatable_searchbtn', !!appliedSearch && 'datatable_searchbtn--active')}>
								<DropdownButton
									label='Search'
									variant='outline'
									placement='left'
									switchMobileWidth={768}
									switchTrailingMobileIcon
									className='btn_control'
									trailingIcon={
										<span className='datatable_searchbtn_icon'>
											<SearchIcon
												variant='default'
												className='svg-dark'
											/>
											{!!appliedSearch && <FullStopIcon className='datatable_searchbtn_dot' />}
										</span>
									}
									dropDownComponent={renderMobileSearch}
								/>
							</div>
						</>
					)}
					{!!filterProps && (
						<DropdownButton
							label={filterProps.label ?? 'Filter'}
							leadingIcon={filterProps.leadingIcon}
							trailingIcon={filterProps.trailingIcon ?? <FilterIcon className='svg-dark' />}
							variant={filterProps.variant ?? 'outline'}
							className={classNameMerger(
								filterProps.className,
								'bg-primary-400-hover color-neutral-50',
								isFilterActive && 'datatable_filterbtn--active',
								'outline-vibrant-hover'
							)}
							dropDownComponent={filterProps.dropDownComponent}
							switchMobileWidth={filterProps.switchMobileWidth ?? 768}
							switchLeadingMobileIcon={filterProps.switchLeadingMobileIcon ?? true}
							onChange={handleOptionClick(filterProps.filterKey ?? '')}
						/>
					)}

					{otherBtnProps?.map((btnItem) => (
						<Button
							key={btnItem.label}
							onClick={btnItem.onClick}
							leadingIcon={btnItem.leadingIcon}
							trailingIcon={btnItem.trailingIcon}
							className={btnItem.className}
							aria-label={btnItem.label}
						>
							{windowSizeProps.width < 768 ? '' : btnItem.label}
						</Button>
					))}

					{exportProps && hasPermission('export') && (
						<Button
							key='export'
							onClick={handleOpenModal({ id: 'fileExport', variant: 'right-sheet' })}
							variant={exportConfig?.variant ?? 'tertiary'}
							trailingIcon={
								exportConfig?.trailingIcon ?? (
									<ExportIcon
										variant='table'
										className='svg-dark'
									/>
								)
							}
							className='tertiary-btn theme-background-none bg-primary-400 tertiary-hover'
							aria-label={exportConfig?.label ?? 'Export'}
							disabled={noRecord}
						>
							{windowSizeProps.width < 768 ? '' : (exportConfig?.label ?? 'Export')}
						</Button>
					)}
				</div>
			</div>

			{!!activeFilters?.length && (
				<div className='datatable_activefilters'>
					{activeFilters.map((chip) => (
						<button
							key={chip.id}
							type='button'
							className='datatable_filterchip'
							onClick={chip.onRemove}
							aria-label={`Remove filter: ${chip.label}`}
						>
							<span className='datatable_filterchip_label'>{chip.label}</span>
							<span
								aria-hidden='true'
								className='datatable_filterchip_remove'
							>
								✕
							</span>
						</button>
					))}

					{onClearFilters && (
						<button
							type='button'
							className='datatable_clearfilters'
							onClick={onClearFilters}
						>
							Clear all
						</button>
					)}
				</div>
			)}
		</>
	);
};
